import React, { useRef, useState, useCallback } from "react";
import { Mic, Loader2, Square } from "lucide-react";

// Reusable mic recorder. Records via MediaRecorder and hands the audio Blob to
// `onAudio(blob)`. `variant`: "pill" (labelled booking button) or "icon" (chat).
export const VoiceButton = ({ onAudio, str, variant = "pill", className = "", "data-testid": testId = "voice-button" }) => {
  const [state, setState] = useState("idle"); // idle | recording | processing
  const mrRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const stop = useCallback(() => {
    try { mrRef.current?.stop(); } catch { /* ignore */ }
  }, []);

  const start = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      alert(str.mic_error);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        setState("processing");
        try { await onAudio(blob); } finally { setState("idle"); }
      };
      mrRef.current = mr;
      mr.start();
      setState("recording");
    } catch {
      setState("idle");
      alert(str.mic_error);
    }
  }, [onAudio, str.mic_error]);

  const toggle = useCallback(() => {
    if (state === "recording") stop();
    else if (state === "idle") start();
  }, [state, start, stop]);

  const busy = state === "processing";
  const rec = state === "recording";

  if (variant === "icon") {
    return (
      <button
        type="button"
        onClick={toggle}
        disabled={busy}
        aria-label={str.speak}
        title={str.speak}
        data-testid={testId}
        data-voice-state={state}
        className={`w-11 h-11 shrink-0 rounded-xl flex items-center justify-center transition-all disabled:opacity-50 ${
          rec ? "bg-red-500 text-white animate-pulse" : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
        } ${className}`}
      >
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : rec ? <Square className="w-4 h-4 fill-current" /> : <Mic className="w-5 h-5" />}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      data-testid={testId}
      data-voice-state={state}
      className={`w-full h-12 rounded-2xl flex items-center justify-center gap-2.5 font-semibold text-sm transition-all disabled:opacity-60 ${
        rec
          ? "bg-red-500 text-white shadow-[0_8px_24px_-6px_rgba(239,68,68,0.5)]"
          : "bg-neutral-900 text-white hover:bg-black hover:-translate-y-0.5"
      } ${className}`}
    >
      {busy ? (
        <><Loader2 className="w-4 h-4 animate-spin" /> {str.processing}</>
      ) : rec ? (
        <><span className="relative flex h-3 w-3"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" /><span className="relative inline-flex rounded-full h-3 w-3 bg-white" /></span> {str.listening}</>
      ) : (
        <><Mic className="w-4 h-4 text-[#F5B400]" /> {str.book}</>
      )}
    </button>
  );
};

export default VoiceButton;
